import { useState, useRef, useLayoutEffect } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { Icon24 } from 'builder/components/Icon'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { i18n as I18n } from 'builder/utils/i18n'
import { UseExampleEventLabels, UseExampleEvents } from 'builder/modules/constants'
import { useResumeExampleContext } from '../ResumeExampleContext'
import {
  categoriesHorizontalGap,
  CategoryContainer,
  CategoryPill,
  CategoryToggle,
  Chevron,
  Div,
} from '../styles'

type Props = {
  hide?: boolean
}

const Categories = ({ hide = false }: Props) => {
  const { categories, selectedCategoryId, setSelectedCategoryId, loadMore } =
    useResumeExampleContext()
  const [maxCategoriesUnexpanded, setMaxCategoriesUnexpanded] = useState<number>(Infinity)
  const [expanded, setExpanded] = useState<boolean>(false)
  const [categoryContainerHeight, setCategoryContainerHeight] = useState<number>(0)
  const [hideToggleButton, setHideToggleButton] = useState<boolean>(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)

  useLayoutEffect(() => {
    const categoryPills = (containerRef.current?.children || []) as HTMLElement[]

    if (!categories.length || !categoryPills.length) return

    const containerWidth = containerRef.current?.offsetWidth || 0
    const toggleWidth = toggleRef.current?.offsetWidth || 0

    let availableWidth = containerWidth - toggleWidth
    let i = 0
    let nextPillWidth = categoryPills[0].offsetWidth + categoriesHorizontalGap

    while (availableWidth > nextPillWidth && i < categoryPills.length - 1) {
      availableWidth = availableWidth - nextPillWidth
      const nextPill = categoryPills[++i]
      nextPillWidth = nextPill ? nextPill.offsetWidth + categoriesHorizontalGap : 0
    }
    if (containerRef?.current) {
      setCategoryContainerHeight(containerRef.current.scrollHeight)
    }

    if (categories.length === i - 1) {
      setHideToggleButton(true)
    }
    setMaxCategoriesUnexpanded(i - 1)
  }, [categories.length])

  const { isPhone } = useMediaQueries()

  const onCategoryClick = (categoryId: number) => {
    setSelectedCategoryId(categoryId)
    loadMore(true)
  }

  return (
    <CategoryContainer
      ref={containerRef}
      expanded={expanded}
      expandedHeight={categoryContainerHeight}
      hide={hide}
    >
      <CategoryPill selected={!selectedCategoryId} onClick={() => onCategoryClick(0)}>
        {I18n.t('builder.use_example.categories.all')}
      </CategoryPill>
      {categories.map((category, index) => {
        if (isPhone || expanded || index < maxCategoriesUnexpanded) {
          return (
            <CategoryPill
              key={category.name}
              selected={category.id === selectedCategoryId}
              onClick={() => {
                trackInternalEvent(UseExampleEvents.clickCategory, {
                  label: UseExampleEventLabels.examplesPrefill,
                  category: category.name,
                })
                onCategoryClick(category.id)
              }}
            >
              {category.name}
            </CategoryPill>
          )
        }

        return null
      })}
      <CategoryToggle
        onClick={() => setExpanded(expanded => !expanded)}
        hidden={hideToggleButton}
        ref={toggleRef}
      >
        <Div>
          {expanded
            ? I18n.t('builder.use_example.categories.see_less')
            : I18n.t('builder.use_example.categories.see_more')}
        </Div>
        <Chevron expanded={expanded}>
          <Icon24.Chevron />
        </Chevron>
      </CategoryToggle>
    </CategoryContainer>
  )
}

export default Categories
